import React, { useId, useRef, useState } from "react";
import { RATING_MAX, RATING_MIN } from "../contract";

/**
 * The star row, in its two roles: a DISPLAY of an average (`readOnly`, or no `onChange`)
 * and an INPUT of a whole-star rating.
 *
 * PRESENTATIONAL, like `Pager` and `LoadFailure`: the caller resolves every string
 * (`label`, `legend`, `starLabel(n)`), so this file imports no dictionary and can be
 * rendered for real in any suite.
 *
 * DISPLAY. One element with `role="img"` and the caller's full-sentence `aria-label`
 * ("4.3 out of 5 stars") — five decorative glyphs are not five facts, and a screen reader
 * that announced "star star star star star" would be reading the ink, not the number.
 * Fractions are drawn by an overlay of filled stars clipped to `value / 5` of the row's
 * width and rounded to the nearest HALF star, the Fiverr convention: 4.25 reads "4.3" in
 * text and shows four and a half stars, never a 5 % sliver on the fifth.
 *
 * INPUT. A `<fieldset>` with a `<legend>` and five visually-hidden NATIVE radios, each
 * labelled by its own `<label>` — which is what makes it a radio group to every assistive
 * technology, with the name, the checked state and the group's label for free. The arrow /
 * Home / End keys are handled HERE, with `preventDefault`, rather than left to the
 * browser: browsers move a radio group with arrows only (Home/End are the WAI-ARIA
 * radio-group pattern, not a native behaviour) and jsdom moves nothing at all — one
 * handler makes the behaviour identical on both, and deterministic under test. Hover
 * previews the fill without changing the value; the value changes only on a check.
 *
 * `variant` is the SURFACE, not a theme preference. Gold `#E6B85C` (`sol-gold`) measures
 * 10.26:1 on `sol-midnight` and about 1.9:1 on white — invisible ink on the review form's
 * cream page. The `light` pair is a dark amber fill with a darker stroke (7.0:1 on white),
 * so the stars keep meaning on both surfaces. Dark surfaces must ask for "dark".
 *
 * @param {number|null} value the rating (display: any number 0..5; input: an integer or 0/null for "none yet")
 * @param {(n: number) => void} [onChange] present → input mode
 * @param {boolean} [readOnly] force display mode even when `onChange` is passed
 * @param {"dark"|"light"} [variant]
 * @param {"sm"|"md"|"lg"} [size]
 * @param {string} [label] DISPLAY: the full-sentence `aria-label`
 * @param {string} [legend] INPUT: the group's visible legend
 * @param {(n: number) => string} [starLabel] INPUT: the label of star n ("3 stars")
 * @param {string} [name] INPUT: the radios' shared `name`
 * @param {boolean} [disabled] INPUT
 * @param {string} [testId] wrapping element's `data-testid`
 * @param {(n: number) => string} [starTestId] INPUT: `data-testid` of radio n
 * @param {string} [className]
 */
const TONES = {
  dark: {
    filled: "text-sol-gold", empty: "text-white/20", stroke: "none",
    ring: "peer-focus-visible:ring-sol-gold/70", legend: "text-white/70",
  },
  light: {
    filled: "text-amber-700", empty: "text-stone-300", stroke: "#78350f",
    ring: "peer-focus-visible:ring-amber-700/60", legend: "text-eh-muted",
  },
};

const SIZES = { sm: "w-3.5 h-3.5", md: "w-5 h-5", lg: "w-8 h-8" };

const STARS = Array.from({ length: RATING_MAX }, (_, i) => i + RATING_MIN);

const Glyph = ({ className, stroke }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    stroke={stroke}
    strokeWidth={stroke === "none" ? 0 : 1}
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6L2.5 9.4l6.6-.8z" />
  </svg>
);

/** Clamp to 0..5 and round to the nearest half — the fraction the overlay draws. */
const halfStars = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(Math.min(RATING_MAX, Math.max(0, n)) * 2) / 2;
};

function StarDisplay({ value, tone, sizeClass, label, testId, className }) {
  const shown = halfStars(value);
  const pct = (shown / RATING_MAX) * 100;
  return (
    <span
      role="img"
      aria-label={label}
      data-testid={testId}
      className={`relative inline-flex leading-none ${className || ""}`}
    >
      <span className={`flex gap-0.5 ${tone.empty}`} aria-hidden="true">
        {STARS.map((n) => <Glyph key={n} className={sizeClass} stroke={tone.stroke} />)}
      </span>
      <span
        className={`absolute inset-y-0 left-0 flex gap-0.5 overflow-hidden ${tone.filled}`}
        style={{ width: `${pct}%` }}
        aria-hidden="true"
        data-fill={String(shown)}
      >
        {STARS.map((n) => <Glyph key={n} className={`${sizeClass} shrink-0`} stroke={tone.stroke} />)}
      </span>
    </span>
  );
}

function StarInput({
  value, onChange, tone, sizeClass, legend, starLabel, name, disabled, testId, starTestId, className,
}) {
  const autoId = useId();
  const groupName = name || `rating-${autoId}`;
  const [hover, setHover] = useState(0);
  const inputs = useRef({});
  const current = Number.isInteger(value) && value >= RATING_MIN ? value : 0;
  const shown = hover || current;

  const move = (next) => {
    const n = Math.min(RATING_MAX, Math.max(RATING_MIN, next));
    onChange(n);
    inputs.current[n]?.focus();
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    const from = current || RATING_MIN - 1;
    switch (e.key) {
      case "ArrowRight": case "ArrowUp": e.preventDefault(); move(from + 1); break;
      case "ArrowLeft": case "ArrowDown": e.preventDefault(); move(current ? from - 1 : RATING_MIN); break;
      case "Home": e.preventDefault(); move(RATING_MIN); break;
      case "End": e.preventDefault(); move(RATING_MAX); break;
      default: break;
    }
  };

  return (
    <fieldset
      data-testid={testId}
      disabled={disabled}
      className={`m-0 p-0 border-0 min-w-0 ${className || ""}`}
    >
      {legend && <legend className={`text-sm mb-1.5 ${tone.legend}`}>{legend}</legend>}
      <div
        className="inline-flex gap-1"
        onKeyDown={onKeyDown}
        onMouseLeave={() => setHover(0)}
      >
        {STARS.map((n) => {
          const id = `${groupName}-${n}`;
          const filled = n <= shown;
          return (
            <span key={n} className="relative">
              <input
                ref={(el) => { inputs.current[n] = el; }}
                type="radio"
                id={id}
                name={groupName}
                value={n}
                checked={current === n}
                onChange={() => onChange(n)}
                disabled={disabled}
                className="peer sr-only"
                data-testid={starTestId ? starTestId(n) : undefined}
              />
              <label
                htmlFor={id}
                onMouseEnter={() => { if (!disabled) setHover(n); }}
                className={`block rounded cursor-pointer transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 ${tone.ring} ${
                  filled ? tone.filled : tone.empty} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
              >
                <Glyph className={sizeClass} stroke={tone.stroke} />
                <span className="sr-only">{starLabel ? starLabel(n) : String(n)}</span>
              </label>
            </span>
          );
        })}
      </div>
    </fieldset>
  );
}

export default function StarRating({
  value,
  onChange,
  readOnly = false,
  variant = "dark",
  size = "md",
  label,
  legend,
  starLabel,
  name,
  disabled = false,
  testId,
  starTestId,
  className,
}) {
  const tone = TONES[variant] || TONES.dark;
  const sizeClass = SIZES[size] || SIZES.md;
  if (readOnly || typeof onChange !== "function") {
    return (
      <StarDisplay
        value={value} tone={tone} sizeClass={sizeClass} label={label} testId={testId} className={className}
      />
    );
  }
  return (
    <StarInput
      value={value} onChange={onChange} tone={tone} sizeClass={sizeClass} legend={legend}
      starLabel={starLabel} name={name} disabled={disabled} testId={testId} starTestId={starTestId}
      className={className}
    />
  );
}
